import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  Plus, RefreshCw, Loader2, Search, Edit, Trash2, X, Save, FileStack, ChevronRight, Link2, Upload, Paperclip, Eye, FileText, FileSpreadsheet, Download, CheckCircle, AlertCircle
} from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { useBreadcrumbs } from '../context/BreadcrumbContext';
import api, { proyectosAPI, subdivisionesAPI } from '../utils/api';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const PLUGIN_API = `${API_BASE}/api/plugins/experiencia-adendas/api`;

const estadoBadge = {
  Borrador: 'bg-gray-100 text-gray-700',
  Emitida: 'bg-blue-100 text-blue-700',
  Aprobada: 'bg-green-100 text-green-700',
  Rechazada: 'bg-red-100 text-red-700',
};

const tipos = ['Características del proyecto', 'Cronograma de pagos', 'Fecha de entrega', 'Fideicomiso', 'Numeración', 'Otros cambios'];
const estados = ['Borrador', 'Emitida', 'Por Negociar', 'Aprobada', 'Aprobada con Negociación', 'Rechazada'];

function getEmptyForm() {
  return {
    proforma: '', proyecto_nombre: '', etapa_nombre: '', departamento: '',
    titular_nombre: '', tipo: 'Características del proyecto', descripcion: '',
    monto: 0, estado: 'Aprobada', negociacion: '', responsable_negociar_id: '',
    budget_id: ''
  };
}

function formatHitoDate(dateStr) {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

function formatFileSize(bytes) {
  if (!bytes) return '0 B';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

/* ── Autocomplete Proyectos ── */
const ProyectoAutocomplete = ({ value, onChange, proyectos }) => {
  const [open, setOpen] = useState(false);
  const [inputVal, setInputVal] = useState(value);
  const wrapRef = useRef(null);

  useEffect(() => { setInputVal(value); }, [value]);
  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const filtered = proyectos.filter(p =>
    p.nombre.toLowerCase().includes(inputVal.toLowerCase()) ||
    (p.codigo_proyecto || '').toLowerCase().includes(inputVal.toLowerCase())
  );

  return (
    <div ref={wrapRef} className="relative">
      <input
        className="w-full px-3 py-2 border border-[#E8E8E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#9d8272] focus:border-transparent"
        value={inputVal}
        onChange={e => { setInputVal(e.target.value); setOpen(true); onChange(e.target.value, null); }}
        onFocus={() => setOpen(true)}
        placeholder="Escriba para buscar proyecto..."
        data-testid="field-proyecto"
      />
      {open && filtered.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-48 overflow-auto bg-white border border-[#E8E8E7] rounded-lg shadow-lg">
          {filtered.map(p => (
            <button key={p.id} type="button"
              className="w-full px-3 py-2 text-left text-sm hover:bg-[#f5f0eb] transition-colors flex items-center gap-2"
              onClick={() => { onChange(p.nombre, p.id); setInputVal(p.nombre); setOpen(false); }}>
              <span className="text-[#9d8272] font-semibold text-xs">{p.codigo_proyecto}</span>
              <span className="text-[#2D2D2D]">{p.nombre}</span>
            </button>
          ))}
        </div>
      )}
      {open && inputVal && filtered.length === 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-[#E8E8E7] rounded-lg shadow-lg px-3 py-3 text-sm text-[#888]">Sin resultados</div>
      )}
    </div>
  );
};

/* ── Componente Principal ── */
const AdendasAcumulativas = () => {
  const { setBreadcrumbs } = useBreadcrumbs();
  const [adendas, setAdendas] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [search, setSearch] = useState('');
  const searchRef = useRef('');
  const searchTimeout = useRef(null);
  const isInitial = useRef(true);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingNumero, setEditingNumero] = useState('');
  const [form, setForm] = useState(getEmptyForm());
  const [saving, setSaving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [proyectos, setProyectos] = useState([]);
  const [etapas, setEtapas] = useState([]);
  const [etapasLoading, setEtapasLoading] = useState(false);
  const [selectedProyectoId, setSelectedProyectoId] = useState(null);
  const [adjuntos, setAdjuntos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [usuariosComerciales, setUsuariosComerciales] = useState([]);
  const fileInputRef = useRef(null);
  const [syncing, setSyncing] = useState(false);
  const [syncUnidades, setSyncUnidades] = useState([]);
  const [syncSelectedUnit, setSyncSelectedUnit] = useState(0);
  const [syncSuccess, setSyncSuccess] = useState(false);
  // Import modal state
  const [showImport, setShowImport] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const importFileRef = useRef(null);

  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };

  useEffect(() => {
    setBreadcrumbs([
      { label: 'Inicio', path: '/' },
      { label: 'Experiencia', path: '#' },
      { label: 'Adendas Acumulativas', path: null }
    ]);
    return () => setBreadcrumbs(null);
  }, [setBreadcrumbs]);

  useEffect(() => {
    proyectosAPI.getAll().then(res => {
      setProyectos((res.data || []).filter(p => p.activo !== false));
    }).catch(() => {});
    fetch(`${PLUGIN_API}/usuarios-comerciales`, { headers }).then(r => r.json()).then(d => setUsuariosComerciales(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  // Cargar etapas cuando cambia el proyecto seleccionado
  useEffect(() => {
    if (!selectedProyectoId) { setEtapas([]); return; }
    setEtapasLoading(true);
    subdivisionesAPI.getByProyecto(selectedProyectoId).then(res => {
      const subs = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      const padres = subs.filter(s => !s.padre_id || s.padre_id === 'None');
      setEtapas(padres);
    }).catch(() => setEtapas([]))
    .finally(() => setEtapasLoading(false));
  }, [selectedProyectoId]);

  const loadAdendas = useCallback(async (silent = false, searchValue = '') => {
    try {
      if (!silent) { if (isInitial.current) setLoading(true); else setTableLoading(true); }
      const params = new URLSearchParams();
      if (searchValue.trim()) params.append('search', searchValue.trim());
      const res = await fetch(`${PLUGIN_API}/adendas?${params}`, { headers });
      const json = await res.json();
      setAdendas(json.data || []);
      isInitial.current = false;
      try { const sRes = await fetch(`${PLUGIN_API}/stats`, { headers }); setStats(await sRes.json()); } catch (_) {}
    } catch (e) {
      if (!silent) toast.error('Error al cargar adendas');
    } finally { if (!silent) { setLoading(false); setTableLoading(false); } }
  }, []);

  useEffect(() => { loadAdendas(false, ''); }, [loadAdendas]);

  const handleSearchChange = useCallback((value) => {
    setSearch(value); searchRef.current = value;
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => loadAdendas(false, value), 400);
  }, [loadAdendas]);

  function handleNew() {
    setForm(getEmptyForm()); setEditingId(null); setEditingNumero('');
    setSelectedProyectoId(null); setEtapas([]); setAdjuntos([]); setSyncUnidades([]); setSyncSelectedUnit(0); setSyncSuccess(false); setShowForm(true);
  }

  function handleEdit(adenda) {
    setForm({
      proforma: adenda.proforma || '', proyecto_nombre: adenda.proyecto_nombre || '',
      etapa_nombre: adenda.etapa_nombre || '',
      departamento: adenda.departamento || '', titular_nombre: adenda.titular_nombre || '',
      tipo: adenda.tipo || 'Características del proyecto', descripcion: adenda.descripcion || '',
      monto: adenda.monto || 0, estado: adenda.estado || 'Borrador', negociacion: adenda.negociacion || adenda.observaciones || '', responsable_negociar_id: adenda.responsable_negociar_id || '',
      budget_id: adenda.budget_id || ''
    });
    setEditingId(adenda.id); setEditingNumero(adenda.numero || '');
    // Intentar encontrar el proyecto_id para cargar etapas
    const proy = proyectos.find(p => p.nombre === adenda.proyecto_nombre);
    setSelectedProyectoId(proy ? proy.id : null);
    setShowForm(true);
    loadAdjuntos(adenda.id);
  }

  function handleProyectoChange(nombre, proyectoId) {
    setForm(f => ({ ...f, proyecto_nombre: nombre, etapa_nombre: '' }));
    setSelectedProyectoId(proyectoId);
  }

  async function handleSyncCRM() {
    const proforma = form.proforma.trim();
    if (!proforma) { toast.error('Ingrese un código de proforma antes de sincronizar.'); return; }
    setSyncing(true);
    setSyncUnidades([]);
    try {
      const resp = await api.get(`/plugins/experiencia-adendas/api/sincronizar-proforma?codigo=${encodeURIComponent(proforma)}`);
      const data = resp.data;

      // Set project
      const proy = proyectos.find(p => p.id === data.proyecto_id_local || p.nombre === data.proyecto_nombre);
      if (proy) {
        setSelectedProyectoId(proy.id);
      }

      // Store units for selector
      const unidades = data.unidades || [];
      setSyncUnidades(unidades);
      setSyncSelectedUnit(0);

      // Default to first unit
      const firstUnit = unidades[0] || {};
      setForm(f => ({
        ...f,
        proyecto_nombre: data.proyecto_nombre || f.proyecto_nombre,
        etapa_nombre: firstUnit.etapa_nombre || '',
        departamento: firstUnit.departamento || '',
        titular_nombre: data.titular_nombre || '',
        budget_id: data.sperant_budget_id ? String(data.sperant_budget_id) : '',
      }));

      toast.success(`Datos sincronizados desde Sperant (${unidades.length} unidad${unidades.length > 1 ? 'es' : ''})`);
      setSyncSuccess(true);
    } catch (err) {
      console.error('CRM sync error:', err);
      // Handle axios error response
      const errorMsg = err.response?.data?.detail || 'Error al sincronizar con Sperant';
      toast.error(errorMsg);
    } finally {
      setSyncing(false);
    }
  }

  function handleSyncUnitChange(idx) {
    const unit = syncUnidades[idx];
    if (!unit) return;
    setSyncSelectedUnit(idx);
    setForm(f => ({
      ...f,
      etapa_nombre: unit.etapa_nombre || f.etapa_nombre,
      departamento: unit.departamento || f.departamento,
    }));
  }

  function handleProformaChange(val) {
    setForm(f => ({ ...f, proforma: val }));
    if (syncSuccess) {
      setSyncSuccess(false);
      setSyncUnidades([]);
      setSyncSelectedUnit(0);
      setForm(f => ({ ...f, proforma: val, proyecto_nombre: '', etapa_nombre: '', departamento: '', titular_nombre: '' }));
      setSelectedProyectoId(null);
    }
  }

  // Derived readonly flags
  const isEditing = !!editingId;
  const isEstadoAprobado = form.estado === 'Aprobada' || form.estado === 'Aprobada con Negociación';
  const crmFieldsLocked = (!isEditing && syncSuccess) || isEditing;
  const allFieldsLocked = isEditing && isEstadoAprobado;

  async function handleSave() {
    if (form.estado === 'Por Negociar' && !form.responsable_negociar_id) {
      toast.error('Debe seleccionar un responsable de negociar');
      return;
    }
    if (form.estado === 'Aprobada con Negociación' && (!form.responsable_negociar_id || !form.negociacion.trim())) {
      toast.error('Para aprobar con negociación debe tener responsable y texto de negociación');
      return;
    }
    setSaving(true);
    try {
      const url = editingId ? `${PLUGIN_API}/adendas/${editingId}` : `${PLUGIN_API}/adendas`;
      const res = await fetch(url, { method: editingId ? 'PUT' : 'POST', headers, body: JSON.stringify(form) });
      const json = await res.json();
      if (json.success || json.data) {
        toast.success(editingId ? 'Adenda actualizada' : 'Adenda creada');
        if (editingId) {
          loadAdendas(false, searchRef.current);
        } else {
          const created = json.data || {};
          setEditingId(created.id || null);
          setEditingNumero(created.numero || '');
          if (created.id) loadAdjuntos(created.id);
          loadAdendas(false, searchRef.current);
        }
      } else { toast.error(json.error || 'Error al guardar'); }
    } catch (e) { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  }

  async function handleDelete() {
    if (!deleteDialog.id) return;
    try {
      await fetch(`${PLUGIN_API}/adendas/${deleteDialog.id}`, { method: 'DELETE', headers });
      toast.success('Adenda eliminada'); setDeleteDialog({ open: false, id: null });
      loadAdendas(false, searchRef.current);
    } catch (e) { toast.error('Error al eliminar'); }
  }

  async function loadAdjuntos(adendaId) {
    try {
      const res = await fetch(`${PLUGIN_API}/upload/${adendaId}`, { headers: { 'Authorization': `Bearer ${token}` } });
      const json = await res.json();
      setAdjuntos(json.adjuntos || []);
    } catch { setAdjuntos([]); }
  }

  async function handleUploadFile(e) {
    const file = e.target.files?.[0];
    if (!file || !editingId) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['jpeg','jpg','png','pdf','docx'].includes(ext)) { toast.error('Formato no permitido. Solo: jpeg, jpg, png, pdf, docx'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('El archivo excede 5 MB'); return; }
    if (adjuntos.length >= 3) { toast.error('Máximo 3 archivos por adenda'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${PLUGIN_API}/upload/${editingId}`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd
      });
      const json = await res.json();
      if (json.success) { toast.success('Archivo subido'); loadAdjuntos(editingId); }
      else { toast.error(json.detail || 'Error al subir'); }
    } catch { toast.error('Error al subir archivo'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  }

  async function handleDeleteFile(idx, nombre) {
    try {
      const res = await fetch(`${PLUGIN_API}/upload/${editingId}/${idx}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      const json = await res.json();
      if (json.success) { toast.success(`${nombre} eliminado`); loadAdjuntos(editingId); }
      else { toast.error('Error al eliminar'); }
    } catch { toast.error('Error al eliminar archivo'); }
  }

  // Import modal functions removed - Proyecto/Etapa now auto-resolved via Sperant CRM

  function openImportModal() {
    setImportResult(null); setShowImport(true);
  }

  async function handleImport() {
    const file = importFileRef.current?.files?.[0];
    if (!file) { toast.error('Seleccione un archivo Excel'); return; }
    setImporting(true); setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${PLUGIN_API}/importar`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd
      });
      const json = await res.json();
      setImportResult(json);
      if (json.success) { toast.success(`${json.total} adendas importadas`); loadAdendas(false, ''); }
    } catch { toast.error('Error al importar'); }
    finally { setImporting(false); }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="adendas-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 lg:h-12 lg:w-12 border-b-2 border-[#9d8272] mx-auto"></div>
          <p className="mt-4 text-sm lg:text-base text-[#5A5A5A]">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 lg:space-y-6" data-testid="adendas-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#2D2D2D]" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="adendas-title">
            Adendas Acumulativas
          </h1>
          <p className="text-sm lg:text-lg text-[#5A5A5A] mt-1 hidden sm:block">Gestiona las adendas acumulativas del sistema de postventa</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={openImportModal} variant="outline" size="sm" className="border-[#9d8272] text-[#9d8272] text-xs lg:text-sm" data-testid="import-excel-button">
            <FileSpreadsheet size={14} className="mr-1" /> Importar Excel
          </Button>
          <Button onClick={handleNew} size="sm" className="bg-[#9d8272] hover:bg-[#8a7162] text-white text-xs lg:text-sm" data-testid="create-adenda-button">
          <Plus size={14} className="mr-1 lg:mr-2" /> Nueva Adenda
        </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {stats.total !== undefined && <StatCard label="Total" value={stats.total} color="#9d8272" />}
          {stats.por_estado && Object.entries(stats.por_estado).map(([k, v]) => (
            <StatCard key={k} label={k} value={v} color={k === 'Aprobada' ? '#16a34a' : k === 'Rechazada' ? '#dc2626' : k === 'Emitida' ? '#2563eb' : '#6b7280'} />
          ))}
        </div>
      )}

      {/* Search */}
      <div className="max-w-2xl">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888]" />
          <input type="text" value={search} onChange={e => handleSearchChange(e.target.value)}
            placeholder="Buscar por numero, proforma, proyecto, etapa, departamento, titular o estado..."
            className="w-full pl-10 pr-4 py-2 border border-[#E8E8E7] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#9d8272] focus:border-transparent text-sm"
            data-testid="adendas-search-input" />
        </div>
      </div>

      {/* Desktop Table */}
      <Card className="bg-white border border-[#E8E8E7] shadow-sm hidden lg:block" data-testid="adendas-desktop-table">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg lg:text-xl font-semibold text-[#2D2D2D]">Listado de Adendas</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => loadAdendas(false, searchRef.current)} disabled={tableLoading} className="text-[#9d8272] hover:text-[#7a6459] hover:bg-[#f5f0eb]" data-testid="refresh-adendas-btn">
              <RefreshCw size={18} className={tableLoading ? 'animate-spin' : ''} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto relative">
            {tableLoading && (
              <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-10 rounded-lg">
                <div className="flex items-center gap-2 text-[#9d8272]"><Loader2 size={20} className="animate-spin" /><span className="text-sm">Buscando...</span></div>
              </div>
            )}
            <table className={`w-full transition-opacity duration-200 ${tableLoading ? 'opacity-50' : 'opacity-100'}`} data-testid="adendas-table">
              <thead>
                <tr className="border-b border-[#E8E8E7] text-left text-sm">
                  <th className="pb-2 font-semibold text-[#5A5A5A]">Numero</th>
                  <th className="pb-2 font-semibold text-[#5A5A5A]">Proforma</th>
                  <th className="pb-2 font-semibold text-[#5A5A5A]">Proyecto</th>
                  <th className="pb-2 font-semibold text-[#5A5A5A]">Etapa</th>
                  <th className="pb-2 font-semibold text-[#5A5A5A]">Depto.</th>
                  <th className="pb-2 font-semibold text-[#5A5A5A]">Titular</th>
                  <th className="pb-2 font-semibold text-[#5A5A5A]">Tipo</th>
                  <th className="pb-2 font-semibold text-[#5A5A5A]">Prox. Hito</th>
                  <th className="pb-2 font-semibold text-[#5A5A5A]">Estado</th>
                  <th className="pb-2 font-semibold text-[#5A5A5A] text-center">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {adendas.map(a => (
                  <tr key={a.id} className="border-b border-[#E8E8E7] hover:bg-[#F5F5F5] transition-colors text-sm h-12" data-testid={`adenda-row-${a.id}`}>
                    <td className="py-2 text-[#9d8272] font-bold align-middle"><div className="truncate max-w-[120px]">{a.numero}</div></td>
                    <td className="py-2 text-[#5A5A5A] align-middle"><div className="truncate max-w-[110px]" title={a.proforma}>{a.proforma || '—'}</div></td>
                    <td className="py-2 text-[#2D2D2D] font-medium align-middle"><div className="truncate max-w-[130px]" title={a.proyecto_nombre}>{a.proyecto_nombre}</div></td>
                    <td className="py-2 text-[#5A5A5A] align-middle"><div className="truncate max-w-[100px]" title={a.etapa_nombre}>{a.etapa_nombre || '—'}</div></td>
                    <td className="py-2 text-[#5A5A5A] align-middle"><div className="truncate max-w-[80px]" title={a.departamento}>{a.departamento}</div></td>
                    <td className="py-2 text-[#5A5A5A] align-middle"><div className="truncate max-w-[120px]" title={a.titular_nombre}>{a.titular_nombre}</div></td>
                    <td className="py-2 text-[#5A5A5A] align-middle">{a.tipo}</td>
                    <td className="py-2 text-[#5A5A5A] align-middle">
                      <HitoCell hito={a.proximo_hito} />
                    </td>
                    <td className="py-2 align-middle">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${estadoBadge[a.estado] || 'bg-gray-100 text-gray-700'}`}>{a.estado}</span>
                    </td>
                    <td className="py-2 align-middle text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(a)} className="text-[#9d8272] hover:text-[#7a6459] h-8 w-8 p-0" data-testid={`edit-adenda-${a.id}`}><Edit size={14} /></Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeleteDialog({ open: true, id: a.id })} className="text-red-500 hover:text-red-700 h-8 w-8 p-0" data-testid={`delete-adenda-${a.id}`}><Trash2 size={14} /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {adendas.length === 0 && (
              <div className="text-center py-12 text-[#888888]"><FileStack size={48} className="mx-auto mb-4 opacity-50" /><p>No hay adendas registradas</p></div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Mobile Cards */}
      <div className="lg:hidden space-y-3 relative">
        {tableLoading && (
          <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-10 rounded-lg min-h-[200px]">
            <div className="flex items-center gap-2 text-[#9d8272]"><Loader2 size={20} className="animate-spin" /><span className="text-sm">Buscando...</span></div>
          </div>
        )}
        <div className="flex items-center justify-between px-1">
          <h2 className="text-base font-semibold text-[#2D2D2D]">Adendas</h2>
          <Button variant="ghost" size="sm" onClick={() => loadAdendas(false, searchRef.current)} disabled={tableLoading} className="text-[#9d8272] hover:text-[#7a6459] hover:bg-[#f5f0eb]">
            <RefreshCw size={18} className={tableLoading ? 'animate-spin' : ''} />
          </Button>
        </div>
        <div className={`transition-opacity duration-200 ${tableLoading ? 'opacity-50' : 'opacity-100'}`}>
          {adendas.length === 0 ? (
            <Card className="bg-white border border-[#E8E8E7] shadow-sm"><CardContent className="py-8"><div className="text-center text-[#888888]"><FileStack size={40} className="mx-auto mb-3 opacity-50" /><p className="text-sm">No hay adendas registradas</p></div></CardContent></Card>
          ) : adendas.map(a => (
            <Card key={a.id} className="bg-white border border-[#E8E8E7] shadow-sm" data-testid={`adenda-card-${a.id}`}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[#9d8272] font-bold text-sm">{a.numero}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${estadoBadge[a.estado] || 'bg-gray-100 text-gray-700'}`}>{a.estado}</span>
                </div>
                {a.proforma && <p className="text-xs text-[#5A5A5A] mb-1">Proforma: {a.proforma}</p>}
                <div className="mb-2">
                  <p className="text-[#2D2D2D] font-medium text-sm truncate">{a.proyecto_nombre}</p>
                  {a.etapa_nombre && <p className="text-[#5A5A5A] text-xs">Etapa: {a.etapa_nombre}</p>}
                  <p className="text-[#5A5A5A] text-xs">Depto. {a.departamento} - {a.tipo}</p>
                </div>
                <div className="flex items-center justify-between text-xs text-[#888888] mb-2">
                  <span className="truncate mr-2">{a.titular_nombre}</span>
                  <HitoCell hito={a.proximo_hito} />
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-[#E8E8E7]">
                  <div className="flex gap-2">
                    <button onClick={() => handleEdit(a)} className="px-2.5 py-1 rounded text-xs font-medium text-[#9d8272] hover:bg-[#f5f0eb] transition-colors">Editar</button>
                    <button onClick={() => setDeleteDialog({ open: true, id: a.id })} className="px-2.5 py-1 rounded text-xs font-medium text-red-500 hover:bg-red-50 transition-colors">Eliminar</button>
                  </div>
                  <ChevronRight size={18} className="text-[#888888]" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid="adenda-form-modal">
          <div className="bg-white rounded-xl w-[640px] max-w-[95vw] max-h-[90vh] shadow-xl flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 pb-3 border-b border-[#E8E8E7]">
              <h2 className="text-lg font-bold text-[#2D2D2D]" data-testid="modal-title">
                {editingId ? `Editar Adenda ${editingNumero}` : 'Nueva Adenda'}
              </h2>
              <Button variant="ghost" size="sm" onClick={() => setShowForm(false)} data-testid="close-form-btn"><X size={16} /></Button>
            </div>
            {/* Scrollable content */}
            <div className="flex-1 overflow-auto p-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold text-[#5A5A5A] mb-1 flex items-center gap-1.5">
                  Proforma
                  {form.budget_id ? (
                    <span className="text-[10px] font-medium text-[#9d8272] bg-[#f5f0eb] px-1.5 py-0.5 rounded" data-testid="badge-budget-id">budget_id: {form.budget_id}</span>
                  ) : (
                    <span className="text-[10px] font-medium text-orange-500 bg-orange-50 px-1.5 py-0.5 rounded" data-testid="badge-budget-id-empty">budget_id: debe sincronizar</span>
                  )}
                </label>
                <div className="flex gap-2">
                  <Input className={`border-[#E8E8E7] focus:ring-[#9d8272] flex-1 ${isEditing ? 'bg-gray-50 text-gray-500' : ''}`} placeholder="Ej: 2026-0004245" value={form.proforma} onChange={e => handleProformaChange(e.target.value)} readOnly={isEditing} data-testid="field-proforma" />
                  <Button variant="outline" size="sm" className={`border-[#9d8272] text-[#9d8272] hover:bg-[#f5f0eb] px-3 shrink-0 ${syncing || allFieldsLocked ? 'opacity-50 cursor-not-allowed' : ''}`} title="Sincronizar con CRM Sperant" data-testid="btn-crm-link" onClick={handleSyncCRM} disabled={syncing || allFieldsLocked}>
                    {syncing ? <Loader2 size={16} className="animate-spin" /> : <Link2 size={16} />}
                  </Button>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-[#5A5A5A] mb-1 block">Proyecto <span className="text-red-500">*</span></label>
                {crmFieldsLocked ? (
                  <Input className="border-[#E8E8E7] bg-gray-50 text-gray-500" value={form.proyecto_nombre} readOnly data-testid="field-proyecto" />
                ) : (
                  <ProyectoAutocomplete value={form.proyecto_nombre} onChange={handleProyectoChange} proyectos={proyectos} />
                )}
              </div>
              <div>
                <label className="text-xs font-semibold text-[#5A5A5A] mb-1 block">Etapa</label>
                {crmFieldsLocked ? (
                  <Input className="border-[#E8E8E7] bg-gray-50 text-gray-500" value={form.etapa_nombre} readOnly data-testid="field-etapa" />
                ) : (
                <select
                  className={`w-full px-3 py-2 border border-[#E8E8E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#9d8272] ${!selectedProyectoId ? 'bg-gray-50 text-[#aaa] cursor-not-allowed' : ''}`}
                  value={form.etapa_nombre}
                  onChange={e => setForm({...form, etapa_nombre: e.target.value})}
                  disabled={!selectedProyectoId}
                  data-testid="field-etapa"
                >
                  <option value="">{etapasLoading ? 'Cargando etapas...' : !selectedProyectoId ? 'Seleccione un proyecto primero' : 'Seleccionar etapa'}</option>
                  {etapas.map(e => <option key={e.id} value={e.nombre}>{e.codigo} - {e.nombre}</option>)}
                </select>
                )}
              </div>
              <div>
                <label className="text-xs font-semibold text-[#5A5A5A] mb-1 block">Departamento <span className="text-red-500">*</span></label>
                <Input className={`border-[#E8E8E7] focus:ring-[#9d8272] ${crmFieldsLocked ? 'bg-gray-50 text-gray-500' : ''}`} value={form.departamento} onChange={e => setForm({...form, departamento: e.target.value})} readOnly={crmFieldsLocked} data-testid="field-departamento" />
                {syncUnidades.length > 1 && (
                  <div className="mt-1">
                    <label className="text-[10px] font-semibold text-[#9d8272] mb-0.5 block">Unidades de la proforma ({syncUnidades.length})</label>
                    <select className="w-full px-2 py-1.5 border border-dashed border-[#9d8272] rounded text-xs bg-[#faf8f6] focus:outline-none focus:ring-1 focus:ring-[#9d8272]"
                      value={syncSelectedUnit} onChange={e => handleSyncUnitChange(Number(e.target.value))} data-testid="sync-unit-selector">
                      {syncUnidades.map((u, i) => <option key={i} value={i}>{u.departamento} — {u.etapa_nombre} ({u.unit_code})</option>)}
                    </select>
                  </div>
                )}
              </div>
              <div>
                <label className="text-xs font-semibold text-[#5A5A5A] mb-1 block">Titular <span className="text-red-500">*</span></label>
                <Input className={`border-[#E8E8E7] focus:ring-[#9d8272] ${crmFieldsLocked ? 'bg-gray-50 text-gray-500' : ''}`} value={form.titular_nombre} onChange={e => setForm({...form, titular_nombre: e.target.value})} readOnly={crmFieldsLocked} data-testid="field-titular" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#5A5A5A] mb-1 block">Tipo <span className="text-red-500">*</span></label>
                <select className={`w-full px-3 py-2 border border-[#E8E8E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#9d8272] ${allFieldsLocked ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''}`} value={form.tipo} onChange={e => setForm({...form, tipo: e.target.value})} disabled={allFieldsLocked} data-testid="field-tipo">
                  {tipos.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-[#5A5A5A] mb-1 block">Estado <span className="text-red-500">*</span></label>
                <select className="w-full px-3 py-2 border border-[#E8E8E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#9d8272]" value={form.estado} onChange={e => setForm({...form, estado: e.target.value})} data-testid="field-estado">
                  {estados.map(e => <option key={e} value={e}>{e}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold text-[#5A5A5A] mb-1 block">Descripcion <span className="text-red-500">*</span></label>
                <textarea className={`w-full px-3 py-2 border border-[#E8E8E7] rounded-md text-sm min-h-[60px] resize-y focus:outline-none focus:ring-2 focus:ring-[#9d8272] ${allFieldsLocked ? 'bg-gray-50 text-gray-500' : ''}`} value={form.descripcion} onChange={e => setForm({...form, descripcion: e.target.value})} readOnly={allFieldsLocked} data-testid="field-descripcion" />
              </div>
              {(form.estado === 'Por Negociar' || form.estado === 'Aprobada con Negociación') && (
                <>
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold text-[#5A5A5A] mb-1 block">Responsable de Negociar <span className="text-red-500">*</span></label>
                  <select className={`w-full px-3 py-2 border border-[#E8E8E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#9d8272] ${allFieldsLocked ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''}`}
                    value={form.responsable_negociar_id} onChange={e => setForm({...form, responsable_negociar_id: e.target.value})} disabled={allFieldsLocked} data-testid="field-responsable-negociar">
                    <option value="">Seleccionar responsable</option>
                    {usuariosComerciales.map(u => <option key={u.id} value={u.id}>{u.nombre_completo} ({u.email})</option>)}
                  </select>
                  {usuariosComerciales.length === 0 && <p className="text-[11px] text-amber-600 mt-1">No hay usuarios con rol Usuario_Comercial.</p>}
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold text-[#5A5A5A] mb-1 block">Negociación</label>
                  <textarea className={`w-full px-3 py-2 border border-[#E8E8E7] rounded-md text-sm min-h-[60px] resize-y focus:outline-none focus:ring-2 focus:ring-[#9d8272] ${allFieldsLocked ? 'bg-gray-50 text-gray-500' : ''}`} value={form.negociacion} onChange={e => setForm({...form, negociacion: e.target.value})} readOnly={allFieldsLocked} placeholder="Tenor de la negociación acordada con el Titular" data-testid="field-negociacion" />
                </div>
                </>
              )}
            </div>
            {/* Adjuntos Section */}
            <div className="border border-[#E8E8E7] rounded-lg p-4" data-testid="adjuntos-section">
              {editingId ? (
                <>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-xs font-semibold text-[#5A5A5A] flex items-center gap-1.5">
                    <Paperclip size={14} /> Archivos Adjuntos ({adjuntos.length}/3)
                  </label>
                  {adjuntos.length < 3 && (
                    <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#9d8272] border border-[#9d8272] rounded-md hover:bg-[#f5f0eb] transition-colors disabled:opacity-50"
                      data-testid="upload-file-btn">
                      {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                      {uploading ? 'Subiendo...' : 'Subir archivo'}
                    </button>
                  )}
                  <input ref={fileInputRef} type="file" className="hidden" accept=".jpeg,.jpg,.png,.pdf,.docx"
                    onChange={handleUploadFile} data-testid="file-upload-input" />
                </div>
                {adjuntos.length === 0 ? (
                  <div className="text-center py-6 border-2 border-dashed border-[#E8E8E7] rounded-lg">
                    <Upload size={24} className="mx-auto text-[#ccc] mb-2" />
                    <p className="text-xs text-[#888]">Sin archivos adjuntos. Sube hasta 3 archivos (jpeg, jpg, png, pdf, docx) de max 5MB.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {adjuntos.map((adj, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-2.5 bg-[#faf8f6] rounded-lg border border-[#E8E8E7]" data-testid={`adjunto-${idx}`}>
                        {['jpeg','jpg','png'].includes(adj.tipo)
                          ? <img src={adj.url} alt="" className="w-8 h-8 rounded object-cover shrink-0 border border-[#E8E8E7]" />
                          : <FileText size={18} className="text-[#9d8272] shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#2D2D2D] truncate">{adj.nombre}</p>
                          <p className="text-xs text-[#888]">{formatFileSize(adj.tamaño)}</p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {adj.url && (
                            <a href={adj.url} target="_blank" rel="noopener noreferrer"
                              className="p-1.5 text-[#9d8272] hover:bg-[#f5f0eb] rounded transition-colors" title="Ver archivo"
                              data-testid={`preview-adjunto-${idx}`}><Eye size={14} /></a>
                          )}
                          <button onClick={() => handleDeleteFile(idx, adj.nombre)}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors" title="Eliminar"
                            data-testid={`delete-adjunto-${idx}`}><Trash2 size={14} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                </>
              ) : (
                <>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-xs font-semibold text-[#5A5A5A] flex items-center gap-1.5">
                    <Paperclip size={14} /> Archivos Adjuntos (0/3)
                  </label>
                  <button disabled
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#9d8272] border border-[#9d8272] rounded-md opacity-50 cursor-not-allowed"
                    data-testid="upload-file-btn-disabled">
                    <Upload size={12} /> Subir archivo
                  </button>
                </div>
                <div className="text-center py-6 border-2 border-dashed border-[#E8E8E7] rounded-lg">
                  <Upload size={24} className="mx-auto text-[#ccc] mb-2" />
                  <p className="text-xs text-[#888]">Guarde la adenda primero para poder adjuntar archivos.</p>
                </div>
                </>
              )}
            </div>
            </div>
            {/* Sticky footer */}
            <div className="flex justify-end gap-2 p-6 pt-4 border-t border-[#E8E8E7] bg-white rounded-b-xl">
              <Button variant="outline" onClick={() => setShowForm(false)} className="border-[#9d8272] text-[#9d8272]" data-testid="cancel-form-btn">Cancelar</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-[#9d8272] hover:bg-[#8a7162] text-white" data-testid="save-adenda-btn">
                {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
                {editingId ? 'Actualizar' : 'Crear'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={o => !o && setDeleteDialog({ open: false, id: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><Trash2 size={20} className="text-red-500" /> Eliminar adenda</AlertDialogTitle>
            <AlertDialogDescription>Esta accion no se puede deshacer. La adenda sera eliminada permanentemente.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#E8E8E7]" data-testid="cancel-delete-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-btn">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import Modal */}
      {showImport && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid="import-modal">
          <div className="bg-white rounded-xl w-[560px] max-w-[95vw] max-h-[90vh] shadow-xl flex flex-col">
            <div className="flex items-center justify-between p-6 pb-3 border-b border-[#E8E8E7]">
              <h2 className="text-lg font-bold text-[#2D2D2D] flex items-center gap-2">
                <FileSpreadsheet size={18} className="text-[#9d8272]" /> Importar Adendas desde Excel
              </h2>
              <Button variant="ghost" size="sm" onClick={() => setShowImport(false)}><X size={16} /></Button>
            </div>
            <div className="flex-1 overflow-auto p-6 space-y-4">
              {!importResult ? (
                <>
                <div>
                  <label className="text-xs font-semibold text-[#5A5A5A] mb-1 block">Archivo Excel (.xlsx)</label>
                  <div className="flex items-center gap-3">
                    <input ref={importFileRef} type="file" accept=".xlsx" className="flex-1 text-sm text-[#5A5A5A] file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border file:border-[#E8E8E7] file:text-xs file:font-medium file:bg-white file:text-[#9d8272] hover:file:bg-[#f5f0eb]" data-testid="import-file-input" />
                  </div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-700">Los campos <b>Proyecto</b>, <b>Etapa</b>, <b>Departamento</b> y <b>Titular</b> se obtienen automáticamente sincronizando cada proforma con el CRM Sperant.</p>
                </div>
                <button onClick={async () => {
                  try {
                    const res = await fetch(`${PLUGIN_API}/plantilla-excel`, { headers });
                    const blob = await res.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a'); a.href = url; a.download = 'plantilla_adendas.xlsx'; a.click(); URL.revokeObjectURL(url);
                  } catch { toast.error('Error al descargar plantilla'); }
                }} className="inline-flex items-center gap-1.5 text-xs text-[#9d8272] hover:underline" data-testid="download-template-link">
                  <Download size={12} /> Descargar plantilla
                </button>
                </>
              ) : importResult.success ? (
                <div className="space-y-3" data-testid="import-success">
                  <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">
                    <CheckCircle size={18} />
                    <span className="font-semibold text-sm">{importResult.total} adendas importadas exitosamente</span>
                  </div>
                  <div className="border border-[#E8E8E7] rounded-lg p-3">
                    <p className="text-xs font-semibold text-[#5A5A5A] mb-1">Proformas sincronizadas con Sperant: <span className="text-[#2D2D2D]">{importResult.proformas_sincronizadas}</span></p>
                  </div>
                  {importResult.resumen?.por_estado && (
                    <div className="border border-[#E8E8E7] rounded-lg p-3">
                      <p className="text-xs font-semibold text-[#5A5A5A] mb-2">Resumen por estado:</p>
                      {Object.entries(importResult.resumen.por_estado).map(([estado, count]) => (
                        <div key={estado} className="flex justify-between text-sm py-1 border-b border-[#f0f0f0] last:border-0">
                          <span className="text-[#5A5A5A]">{estado}</span>
                          <span className="font-semibold text-[#2D2D2D]">{count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {importResult.resumen?.por_tipo && (
                    <div className="border border-[#E8E8E7] rounded-lg p-3">
                      <p className="text-xs font-semibold text-[#5A5A5A] mb-2">Resumen por tipo:</p>
                      {Object.entries(importResult.resumen.por_tipo).map(([tipo, count]) => (
                        <div key={tipo} className="flex justify-between text-sm py-1 border-b border-[#f0f0f0] last:border-0">
                          <span className="text-[#5A5A5A]">{tipo}</span>
                          <span className="font-semibold text-[#2D2D2D]">{count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3" data-testid="import-errors">
                  <div className="flex items-center gap-2 text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
                    <AlertCircle size={18} />
                    <span className="font-semibold text-sm">{importResult.errores?.length || 0} errores encontrados</span>
                  </div>
                  <div className="border border-red-200 rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-red-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-red-700 font-semibold">Fila</th>
                          <th className="px-3 py-2 text-left text-red-700 font-semibold">Campo</th>
                          <th className="px-3 py-2 text-left text-red-700 font-semibold">Detalle</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(importResult.errores || []).map((err, i) => (
                          <tr key={i} className="border-t border-red-100">
                            <td className="px-3 py-1.5 font-mono text-red-600">{err.fila}</td>
                            <td className="px-3 py-1.5 font-medium text-red-700">{err.campo}</td>
                            <td className="px-3 py-1.5 text-red-600">{err.detalle}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 p-6 pt-4 border-t border-[#E8E8E7] bg-white rounded-b-xl">
              <Button variant="outline" onClick={() => setShowImport(false)} className="border-[#9d8272] text-[#9d8272]">Cerrar</Button>
              {!importResult && (
                <Button onClick={handleImport} disabled={importing} className="bg-[#9d8272] hover:bg-[#8a7162] text-white" data-testid="import-submit-btn">
                  {importing ? <Loader2 size={16} className="animate-spin mr-2" /> : <Upload size={14} className="mr-2" />}
                  {importing ? 'Importando...' : 'Importar'}
                </Button>
              )}
              {importResult && !importResult.success && (
                <Button onClick={() => setImportResult(null)} className="bg-[#9d8272] hover:bg-[#8a7162] text-white">Reintentar</Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Celda Próximo Hito ── */
const HitoCell = ({ hito }) => {
  if (!hito) return <span className="text-[#ccc]">—</span>;
  return (
    <span className="text-xs whitespace-nowrap" data-testid="hito-cell">
      <span className="text-[#9d8272] font-semibold cursor-help" title={hito.nombre}>{hito.codigo}</span>
      <span className="text-[#5A5A5A]">: {formatHitoDate(hito.fecha)}</span>
    </span>
  );
};

const StatCard = ({ label, value, color }) => (
  <Card className="bg-white border border-[#E8E8E7] shadow-sm">
    <CardContent className="p-3 flex items-center gap-3">
      <div className="w-2 h-10 rounded-full" style={{ backgroundColor: color }} />
      <div>
        <p className="text-lg font-bold text-[#2D2D2D]">{value}</p>
        <p className="text-xs text-[#888888]">{label}</p>
      </div>
    </CardContent>
  </Card>
);

export default AdendasAcumulativas;
